import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from 'react';

export type FlagToShareActions = {
	contentId: string;
	openShareDialog: () => void;
	copyShareLink: () => void;
};

export type RegisterFlagToShareActions = (actions: FlagToShareActions) => () => void;

type Registration = {
	token: object;
	actions: FlagToShareActions;
};

type FlagToShareActionsContextValue = {
	current: Registration | null;
	register: RegisterFlagToShareActions;
};

const FlagToShareActionsContext = createContext<FlagToShareActionsContextValue | undefined>(
	undefined,
);

const useFlagToShareActionsContext = (): FlagToShareActionsContextValue => {
	const context = useContext(FlagToShareActionsContext);
	if (!context) {
		throw new Error('Flag-to-Share action hooks must be used within FlagToShareActionsProvider');
	}
	return context;
};

export const FlagToShareActionsProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	const [registration, setRegistration] = useState<Registration | null>(null);

	const register = useCallback<RegisterFlagToShareActions>((actions) => {
		const token = {};
		setRegistration({ token, actions });

		return () => {
			setRegistration((current) => (current?.token === token ? null : current));
		};
	}, []);

	const value = useMemo(() => ({ current: registration, register }), [registration, register]);

	return (
		<FlagToShareActionsContext.Provider value={value}>
			{children}
		</FlagToShareActionsContext.Provider>
	);
};

export const useRegisterFlagToShareActions = (): RegisterFlagToShareActions =>
	useFlagToShareActionsContext().register;

export const useCurrentFlagToShareActions = (): FlagToShareActions | undefined =>
	useFlagToShareActionsContext().current?.actions;
